import {createAsyncThunk, createSlice, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "app/realtime-store/redux-store";
import axios from "axios";
import type {Identifiable} from "common/contracts/paginated-response";
import PaginatedResponse from "common/contracts/paginated-response";
import Guid from "common/values/guid/guid";
import type {Draft} from "immer";
import {isDraft, original} from "immer";
import PermissionsAPIService, {
  PermissionsByGranteeId
} from "permissions/entities/permission/api/permissions-api-service";
import Permission from "permissions/entities/permission/permission";
import {useSelector} from "react-redux";
import Session from "users/session/session";
import PermissionQuery from "../api/request-contracts/permission-query";

type PermissionStoreState = {
  byId: {
    entries: Record<string, Permission>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<Permission>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byGranteeId: {
    entries: Record<string, PaginatedResponse<PermissionsByGranteeId>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, PermissionQuery>;
  };
};

const initialState: PermissionStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  byGranteeId: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

const asPlain = <T>(v: T | Draft<T>): T => (isDraft(v) ? (original(v) as T) : (v as T));

function withData<T extends Identifiable>(
  page: PaginatedResponse<T> | Draft<PaginatedResponse<T>>,
  data: T[]
): PaginatedResponse<T> {
  const base = asPlain(page);
  const copy = Object.assign(Object.create(Object.getPrototypeOf(base)), base);
  copy.data = data;
  return copy;
}

function replacePermissionEverywhere(
  state: Draft<PermissionStoreState>,
  matchId: Guid,
  mapper: (p: Permission | Draft<Permission>) => Permission
) {
  const isMatch = (p?: Permission | Draft<Permission>) => p?.id?.isEqualTo?.(matchId);

  // byId
  const idKey = matchId.value;
  if (state.byId.entries[idKey]) {
    state.byId.entries[idKey] = mapper(state.byId.entries[idKey]);
  }

  // byQuery
  for (const key of Object.keys(state.byQuery.entries)) {
    const pageDraft = state.byQuery.entries[key];
    if (!pageDraft?.data) continue;

    const page = asPlain(pageDraft) as PaginatedResponse<Permission>;
    const newData: Permission[] = page.data.map((p) => (isMatch(p) ? mapper(p) : p));
    state.byQuery.entries[key] = withData<Permission>(pageDraft, newData);
  }

  // byGranteeId
  for (const key of Object.keys(state.byGranteeId.entries)) {
    const pageDraft = state.byGranteeId.entries[key];
    if (!pageDraft?.data) continue;

    const page = asPlain(pageDraft) as PaginatedResponse<PermissionsByGranteeId>;
    const newData: PermissionsByGranteeId[] = page.data.map((group) => {
      const g = asPlain(group);
      const perms: Permission[] = g.recentPermissionsPreview.map((p) => (isMatch(p) ? mapper(p) : p));
      // Preserve prototype
      const gCopy = Object.assign(Object.create(Object.getPrototypeOf(g)), g);
      gCopy.recentPermissionsPreview = perms;
      return gCopy;
    });

    state.byGranteeId.entries[key] = withData<PermissionsByGranteeId>(pageDraft, newData);
  }
}

export const populatePermissionsByQuery = createAsyncThunk(
  "permissions/getPermissionsByQuery",
  async (
    { session, query }: { session: Session; query: PermissionQuery },
    thunkAPI
  ) => {
    const apiService = new PermissionsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissions(
        query,
        abortController
      );
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populatePermissionsByGranteeId = createAsyncThunk(
  "permissions/getPermissionsByGranteeId",
  async (
    { session, query }: { session: Session; query: PermissionQuery },
    thunkAPI
  ) => {
    if (!query) return;
    const apiService = new PermissionsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissionsByGrantee(
        query,
        abortController
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request cancelled");
      }
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const addPermission = createAsyncThunk<
  Permission,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/addPermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.addPermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const addPermissions = createAsyncThunk<
  Permission[],
  { session: Session; permissions: Permission[] },
  { rejectValue: string }
>("permissions/addPermissions", async ({ session, permissions }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.bulkAddPermissions(permissions);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const disablePermission = createAsyncThunk<
  Permission,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/disablePermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.disablePermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const enablePermission = createAsyncThunk<
  Permission,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/enablePermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.enablePermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    updatePermissionInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: PermissionQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      populatePermissionsByQuery.pending,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populatePermissionsByQuery.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = false;
        state.byQuery.entries[query] = action.payload;
      }
    );
    builder.addCase(
      populatePermissionsByQuery.rejected,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.payload as string;
      }
    );

    builder.addCase(
      populatePermissionsByGranteeId.pending,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byGranteeId.loading[query] = true;
        state.byGranteeId.error[query] = null;
      }
    );
    builder.addCase(
      populatePermissionsByGranteeId.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byGranteeId.loading[query] = false;
        if (action.payload !== undefined) {
          state.byGranteeId.entries[query] = action.payload;
        }
      }
    );
    builder.addCase(
      populatePermissionsByGranteeId.rejected,
      (state, action) => {
        if (!action.meta.arg.query?.filterParameters) {
          return;
        }
        const query = action.meta.arg.query.cacheKey();
        state.byGranteeId.loading[query] = false;
        state.byGranteeId.error[query] = action.payload as string;
      }
    );

    // add.pending - optimistic set
    builder.addCase(addPermission.pending, (state, action) => {
      const id = action.meta.arg.permission.id;
      if (id) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
        replacePermissionEverywhere(state, id, () => action.meta.arg.permission.clone());
      }
      // else: no id yet, skip replace; rely on fulfilled below to insert
    });
    // add.fulfilled - server truth
    builder.addCase(addPermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.permission.id;
      if (!id) return;

      state.byId.entries[id.value] = updated.clone();

      let replaced = false;
      // try replacement first
      replacePermissionEverywhere(state, id, () => {
        replaced = true;
        return updated.clone();
      });

      // if nothing got replaced, append to current pages
      if (!replaced) {
        for (const key of Object.keys(state.byGranteeId.entries)) {
          const page = state.byGranteeId.entries[key];
          if (!page?.data) continue;

          const newGroups = page.data.map((g) => {
            // decide whether this permission belongs in g
            // (typically match on g.granteeUserId and whether it should show in preview)
            if (g.granteeUserId?.isEqualTo?.(updated.granteeUserId)) {
              const gCopy = Object.assign(Object.create(Object.getPrototypeOf(g)), g);
              gCopy.recentPermissionsPreview = [...g.recentPermissionsPreview, updated.clone()];
              return gCopy;
            }
            return g;
          });

          state.byGranteeId.entries[key] = withData(page, newGroups);
        }
      }

      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = null;
    });
    // add.rejected – rollback
    builder.addCase(addPermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      replacePermissionEverywhere(state, orig.id, () => orig.clone());

      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // addPermissions.pending – optimistic set
    builder.addCase(addPermissions.pending, (state, action) => {
      for (const perm of action.meta.arg.permissions) {
        const id = perm.id;
        if (id) {
          state.byId.loading[id.value] = true;
          state.byId.error[id.value] = null;
          replacePermissionEverywhere(state, id, () => perm.clone());
        }
      }
    });
    // addPermissions.fulfilled – server truth
    builder.addCase(addPermissions.fulfilled, (state, action) => {
      const updatedPermissions = action.payload;
      for (const updated of updatedPermissions) {
        const id = updated?.id;
        if (!id) continue;
        state.byId.entries[id.value] = updated.clone();

        let replaced = false;

        // try replacement first
        replacePermissionEverywhere(state, id, () => {
          replaced = true;
          return updated.clone();
        });
        // if nothing got replaced, append to current pages
        if (!replaced) {
          for (const key of Object.keys(state.byGranteeId.entries)) {
            const page = state.byGranteeId.entries[key];
            if (!page?.data) continue;
            const newGroups = page.data.map((g) => {
              // decide whether this permission belongs in g
              // (typically match on g.granteeUserId and whether it should show in preview)
              if (g.granteeUserId?.isEqualTo?.(updated.granteeUserId)) {
                const gCopy = Object.assign(Object.create(Object.getPrototypeOf(g)), g);
                gCopy.recentPermissionsPreview = [...g.recentPermissionsPreview, updated.clone()];
                return gCopy;
              }
              return g;
            });
            state.byGranteeId.entries[key] = withData(page, newGroups);
          }
        }

        state.byId.loading[id.value] = false;
        state.byId.error[id.value] = null;
      }
    });
    // addPermissions.rejected – rollback
    builder.addCase(addPermissions.rejected, (state, action) => {
      for (const perm of action.meta.arg.permissions) {
        const id = perm.id;
        if (id) {
          replacePermissionEverywhere(state, id, () => perm.clone());
        }
      }
    });

    // disable.pending – optimistic set
    builder.addCase(disablePermission.pending, (state, action) => {
      const id = action.meta.arg.permission.id;
      if (!id) return;
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;

      replacePermissionEverywhere(state, id, (p) => {
        const prev = asPlain(p) as Permission;
        const next = prev.clone();
        next.disabledDate = prev.disabledDate ?? prev.createdDate;
        return next;
      });
    });
    // disable.fulfilled – server truth
    builder.addCase(disablePermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const permId = updated?.id ?? action.meta.arg.permission.id;
      if (!permId) return;

      replacePermissionEverywhere(state, permId, (p) => {
        const prev = asPlain(p) as Permission;
        const next = prev.clone();
        next.disabledDate = updated?.disabledDate;
        return next;
      });

      state.byId.loading[permId.value] = false;
      state.byId.error[permId.value] = null;
    });
    // disable.rejected – rollback
    builder.addCase(disablePermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      replacePermissionEverywhere(state, orig.id, () => orig.clone());

      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });
    // enable.pending – optimistic
    builder.addCase(enablePermission.pending, (state, action) => {
      const id = action.meta.arg.permission.id;
      if (!id) return;
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;

      replacePermissionEverywhere(state, id, (p) => {
        const prev = asPlain(p) as Permission;
        const next = prev.clone();
        next.disabledDate = undefined;
        return next;
      });
    });
    // enable.fulfilled – server truth
    builder.addCase(enablePermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const permId = updated?.id ?? action.meta.arg.permission.id;
      if (!permId) return;

      replacePermissionEverywhere(state, permId, (p) => {
        const prev = asPlain(p) as Permission;
        const next = prev.clone();
        next.disabledDate = updated?.disabledDate;
        return next;
      });

      state.byId.loading[permId.value] = false;
      state.byId.error[permId.value] = null;
    });
    // enable.rejected – rollback
    builder.addCase(enablePermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      replacePermissionEverywhere(state, orig.id, () => orig.clone());

      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    builder.addMatcher(
      isAnyOf(
        disablePermission.fulfilled,
        disablePermission.rejected,
        enablePermission.fulfilled,
        enablePermission.rejected
      ),
      (state, action) => {
        const argId = action?.meta?.arg?.permission?.id;
        if (argId) {
          state.byId.loading[argId.value] = false;
        }
      }
    );
  }
});

export const {
  updatePermissionInterfaceQuery
} = permissionsSlice.actions;

export const getPermissionsByQuery = (query: PermissionQuery) => {
  return useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    return state.permissions.byQuery.entries[
      query.cacheKey()
    ];
  });
};
export const getPermissionsByGranteeId = (query: PermissionQuery) => {
  return useSelector((state: RootState) => {
    if (!query) return undefined;
    return state.permissions.byGranteeId.entries[query.cacheKey()];
  });
};
export const getIsLoadingPermissionsByQuery = (query: PermissionQuery): boolean =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.permissions.byQuery.loading[
      query.cacheKey()
    ] ?? false;
  });
export const getIsLoadingPermissionsByGranteeId = (query: PermissionQuery): boolean =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.permissions.byGranteeId.loading[
      query.cacheKey()
    ] ?? false;
  });
export const getErrorLoadingPermissionsByQuery = (
  query: PermissionQuery
) =>
  useSelector(
    (state: RootState) => {
      if (!query) return false;
      return state.permissions.byQuery.error[query.cacheKey()] ?? false;
    }
  );
export const getErrorLoadingPermissionsByGranteeId = (
  query: PermissionQuery
) =>
  useSelector(
    (state: RootState) => {
      if (!query) return false;
      return state.permissions.byGranteeId.error[query.cacheKey()] ?? false;
    }
  );

export const getPermissionById = (id: Guid) =>
  useSelector((state: RootState) => state.permissions.byId.entries[id.value]);
export const getIsLoadingPermissionById = (id: Guid) =>
  useSelector((state: RootState) => state.permissions.byId.loading[id.value]);
export const getErrorLoadingPermissionById = (id: Guid) =>
  useSelector((state: RootState) => state.permissions.byId.error[id.value]);

export const getPermissionsQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.permissions.interfaceQueries.entries[interfaceName]
  );

export const permissionIsLoading = (id: Guid) =>
  useSelector((state: RootState) => state.permissions.byId.loading[id.value]);

export default permissionsSlice;
